import React, { useEffect, useState } from 'react';
import { Lightbulb, Loader2, PawPrint, BellPlus, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const defaultDate = () => new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

export const BreedTips = ({ cat }) => {
  const breed = cat?.breed?.trim();
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);
  const [dateVal, setDateVal] = useState(defaultDate());
  const [typeVal, setTypeVal] = useState('other');
  const [savingIdx, setSavingIdx] = useState(null);
  const [savedIdx, setSavedIdx] = useState([]);

  useEffect(() => {
    let active = true;
    setTips([]);
    setSavedIdx([]);
    setOpenIdx(null);
    if (!breed) return;
    setLoading(true);
    api.get(`/ai/breed-tips`, { params: { breed } })
      .then((r) => { if (active) setTips(r.data?.tips || []); })
      .catch(() => { if (active) setTips([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [breed]);

  const openPicker = (idx) => { setOpenIdx(idx); setDateVal(defaultDate()); setTypeVal('other'); };

  const saveAsReminder = async (tip, idx) => {
    setSavingIdx(idx);
    try {
      await api.post('/reminders', {
        title: `${tip.title} — ${cat?.name}`,
        type: typeVal,
        date: dateVal,
        notes: tip.tip,
        cat_id: cat?.id,
      });
      setSavedIdx((p) => [...p, idx]);
      setOpenIdx(null);
      toast.success(`Added to care reminders for ${new Date(dateVal + 'T00:00:00').toLocaleDateString()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add reminder');
    } finally { setSavingIdx(null); }
  };

  if (!breed) return null;

  return (
    <div className="mt-5 rounded-2xl bg-white p-4 card-soft" data-testid="breed-tips">
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent">
          <Lightbulb className="h-4.5 w-4.5 text-[hsl(var(--coral))]" />
        </div>
        <div>
          <h3 className="font-extrabold leading-tight">Care tips for your {breed}</h3>
          <p className="text-xs text-muted-foreground">AI vet guidance tailored to {cat?.name}'s breed</p>
        </div>
      </div>

      {loading && (
        <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="breed-tips-loading">
          <Loader2 className="h-4 w-4 animate-spin" />Fetching {breed} tips…
        </div>
      )}

      {!loading && tips.length > 0 && (
        <ul className="mt-3 space-y-3">
          {tips.map((t, i) => (
            <li key={i} className="flex gap-3" data-testid="breed-tip-item">
              <PawPrint className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--coral))]" />
              <div className="flex-1">
                <p className="text-sm font-bold">{t.title}</p>
                <p className="text-sm text-muted-foreground">{t.tip}</p>

                {savedIdx.includes(i) ? (
                  <span className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-green-600" data-testid={`breed-tip-saved-${i}`}>
                    <Check className="h-3.5 w-3.5" />Added to reminders
                  </span>
                ) : openIdx === i ? (
                  <div className="mt-2 rounded-xl border border-border bg-secondary/30 p-2.5" data-testid={`breed-tip-picker-${i}`}>
                    <label className="text-xs font-semibold text-muted-foreground">Reminder type</label>
                    <select
                      value={typeVal}
                      onChange={(e) => setTypeVal(e.target.value)}
                      data-testid={`breed-tip-type-${i}`}
                      className="mt-1 h-10 w-full rounded-lg border border-input bg-white px-2.5 text-sm font-semibold outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]"
                    >
                      <option value="vaccine">Vaccine</option>
                      <option value="vet-visit">Vet visit</option>
                      <option value="medication">Medication</option>
                      <option value="other">Other</option>
                    </select>
                    <label className="mt-2 block text-xs font-semibold text-muted-foreground">Remind me on</label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="date"
                        value={dateVal}
                        min={today()}
                        onChange={(e) => setDateVal(e.target.value)}
                        data-testid={`breed-tip-date-${i}`}
                        className="h-10 flex-1 rounded-lg border border-input bg-white px-2.5 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]"
                      />
                      <button
                        onClick={() => saveAsReminder(t, i)}
                        disabled={savingIdx === i || !dateVal}
                        data-testid={`breed-tip-confirm-${i}`}
                        className="coral-btn flex h-10 shrink-0 items-center justify-center gap-1 rounded-lg px-3 text-sm font-bold text-white disabled:opacity-60"
                      >
                        {savingIdx === i ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                      </button>
                      <button
                        onClick={() => setOpenIdx(null)}
                        data-testid={`breed-tip-cancel-${i}`}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-white text-muted-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => openPicker(i)}
                    data-testid={`breed-tip-save-${i}`}
                    className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-[hsl(var(--coral))] hover:underline"
                  >
                    <BellPlus className="h-3.5 w-3.5" />Add to reminders
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {!loading && tips.length === 0 && (
        <p className="mt-3 text-sm text-muted-foreground">No breed tips available right now. Ask our AI vet anything on the Ask Vet tab.</p>
      )}

      <p className="mt-3 text-[11px] text-muted-foreground">General guidance, not a diagnosis. See a vet for concerns specific to {cat?.name}.</p>
    </div>
  );
};

export default BreedTips;
